import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBasket } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart, formatPrice } from "../context/CartContext";

export const CartDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, products, updateQty, totals, totalLabel, currency, cartOpen, setCartOpen } = useCart();

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-md bg-brand-cream flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-3xl text-brand-dark uppercase text-start">{t("cart.title")}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-brand-brown">
              <ShoppingBasket size={48} className="mx-auto opacity-30" />
              <p data-testid="cart-empty-message" className="mt-4 text-sm font-semibold">{t("cart.empty")}</p>
            </div>
          ) : (
            items.map((i) => {
              const p = products.find((pr) => pr.product_id === i.product_id);
              if (!p) return null;
              return (
                <div key={i.product_id} data-testid={`cart-item-${i.product_id}`}
                  className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-brand-green/15">
                  <div className="w-14 h-14 rounded-xl bg-brand-yellow flex items-center justify-center font-heading text-xl text-brand-dark shrink-0">
                    {p.weight_kg}KG
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-sm text-brand-dark">Agrodyke {p.weight_kg} kg</div>
                    <div className="text-sm text-brand-orange font-bold">{formatPrice(p, currency, i.quantity)}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <button data-testid={`cart-decrease-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity - 1)}
                        disabled={i.quantity <= 5}
                        className="w-7 h-7 rounded-full bg-brand-cream border border-brand-green/20 flex items-center justify-center text-brand-dark hover:bg-brand-yellow transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        <Minus size={12} />
                      </button>
                      <span data-testid={`cart-qty-${i.product_id}`} className="text-sm font-bold min-w-[2.5rem] text-center">{i.quantity} kg</span>
                      <button data-testid={`cart-increase-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 1)}
                        className="w-7 h-7 rounded-full bg-brand-cream border border-brand-green/20 flex items-center justify-center text-brand-dark hover:bg-brand-yellow transition-colors">
                        <Plus size={12} />
                      </button>
                      <button data-testid={`cart-add5-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 5)}
                        className="px-2 h-7 rounded-full bg-brand-cream border border-brand-green/20 text-[11px] font-bold text-brand-dark hover:bg-brand-yellow transition-colors">
                        +5
                      </button>
                      <button data-testid={`cart-add10-${i.product_id}`} onClick={() => updateQty(i.product_id, i.quantity + 10)}
                        className="px-2 h-7 rounded-full bg-brand-cream border border-brand-green/20 text-[11px] font-bold text-brand-dark hover:bg-brand-yellow transition-colors">
                        +10
                      </button>
                    </div>
                    <p className="text-[10px] font-semibold text-brand-green mt-1">{t("products.minQty")}</p>
                  </div>
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => updateQty(i.product_id, 0)}
                    className="text-brand-brown/50 hover:text-red-500 transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
              );
            })
          )}
        </div>
        {items.length > 0 && (
          <div className="border-t border-brand-green/15 pt-4 space-y-4">
            <div className="flex justify-between items-center">
              <span className="font-bold text-brand-brown">{t("cart.total")}</span>
              <span data-testid="cart-total" className="font-heading text-3xl text-brand-dark">{totalLabel}</span>
            </div>
            <button data-testid="cart-checkout-button"
              onClick={() => { setCartOpen(false); navigate("/checkout"); }}
              className="w-full px-6 py-4 rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors">
              {t("cart.checkout")}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
